import React, { useEffect, useState } from "react";
import "./style.css";
import { AuditDetail } from "../../models/AuditDetail";
import AuditService from "../../services/audit.service";
import { Audit } from "../../models/Audit";
import { AuditModule } from "../../models/AuditModule";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import ReportService from "../../services/report.service";
import { AuditQuestion } from "../../models/AuditQuestion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretLeft,
  faSave,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import ModalAuditConfirm from "./ModalAuditConfirm";
import useUser from "../App/useUser";
import { useLoading } from "../App/LoadingContext";

const LoadingOverlay = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1200,
      color: "white",
      flexDirection: "column",
    }}
  >
    <Spinner animation="border" role="status" variant="light" className="mb-3">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
    <h4>Exportando reporte...</h4>
    <p>Por favor, espere un momento.</p>
  </div>
);

const renderMultiColumnTable = (detail: AuditDetail) => {
  try {
    const columns = JSON.parse(detail.auditAnswer?.answerDesc || "[]");
    const rows = JSON.parse(detail.answerText || "[]");
    if (rows.length === 0)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;
    return (
      <Table striped bordered hover responsive size="sm" className="mt-2">
        <thead className="table-secondary">
          <tr>
            {columns.map((col: string) => (
              <th key={col} className="text-center">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, rowIndex: number) => (
            <tr key={rowIndex} className="align-middle">
              {columns.map((col: string) => (
                <td key={col} className="text-center">
                  {row[`"${col}"`] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  } catch (e) {
    return <p className="text-danger">Error al mostrar datos de tabla.</p>;
  }
};

const renderMatrixTable = (detail: AuditDetail) => {
  try {
    const desc = detail.auditAnswer?.answerDesc;
    if (!desc || !desc.includes("}"))
      return <p className="text-muted fst-italic">Sin respuesta.</p>;
    const parts = desc.split("}");
    if (parts.length < 2)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;
    const rowsDef = JSON.parse(parts[0]);
    const colsDef = JSON.parse(parts[1]);
    const answers = detail.answerText
      ? JSON.parse(detail.answerText).map((r: string) => JSON.parse(r))
      : [];
    if (answers.length === 0)
      return <p className="text-muted fst-italic">Sin respuesta.</p>;
    return (
      <Table bordered hover responsive size="sm" className="mt-2">
        <thead className="table-secondary">
          <tr>
            <th style={{ minWidth: "150px" }}></th>
            {colsDef.map((colName: string, colIndex: number) => (
              <th key={colIndex} className="text-center">
                {colName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsDef.map((rowName: string, rowIndex: number) => (
            <tr key={rowIndex} className="align-middle">
              <td className="table-light fw-bold">{rowName}</td>
              {colsDef.map((_: string, colIndex: number) => (
                <td key={colIndex} className="text-center">
                  {answers[rowIndex]?.[colIndex] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  } catch (e) {
    return <p className="text-danger">Error al mostrar datos de matriz.</p>;
  }
};

const Summary = () => {
  const { user } = useUser();
  const [auditDetails, setAuditDetails] = useState<AuditDetail[]>([]);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [modules, setModules] = useState<AuditModule[]>([]);
  const [allQuestions, setAllQuestions] = useState<AuditQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [validated, setValidated] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const locationRouter = useLocation();
  const navigate = useNavigate();
  const auditId =
    locationRouter.state?.auditId ||
    parseInt(localStorage.getItem("auditId") || "-1");
  const [showModalConfirm, setShowModalConfirm] = useState(true);
  const { wrapWithLoading } = useLoading();
  const handleAgree = () => {
    setShowModalConfirm(false);
  };

  useEffect(() => {
    async function loadInitialData() {
      if (!auditId || auditId === -1) return;
      setIsLoading(true);
      try {
        const localAudit = await AuditService.getQuestionaryById(auditId);
        setAudit(localAudit);
        const modulesData = await AuditService.getModules(
          localAudit.policyTypeId
        );
        modulesData.sort((a, b) => a.position - b.position);
        modulesData.forEach((module) => {
          module.sections
            ?.sort((a, b) => a.position - b.position)
            .forEach((section) => {
              section.subsections?.sort((a, b) => a.position - b.position);
            });
        });
        setModules(modulesData);
        const [questionsData, detailsData] = await Promise.all([
          AuditService.getAllQuestionsForType(localAudit.policyTypeId),
          AuditService.getQuestionaryDetail(auditId),
        ]);
        setAllQuestions(questionsData);
        setAuditDetails(detailsData);
      } catch (error) {
        console.error("Error al cargar datos del resumen:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadInitialData();
  }, [auditId]);

  const handleCommentChanged = (detailId?: number, comment?: string) => {
    if (detailId === undefined) return;
    const updatedDetails = auditDetails.map((detail) =>
      detail.id === detailId ? { ...detail, comment: comment } : detail
    );
    setAuditDetails(updatedDetails);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setErrorTitle("Error de Validación");
      setErrorMessage("Por favor, complete todos los campos requeridos.");
      setShowValidationError(true);
      return;
    }
    setValidated(true);

    const submissionProcess = async () => {
      try {
        const saveResponse = await AuditService.saveComments(auditDetails);
        if (!saveResponse.ok) {
          throw new Error("La respuesta de guardado no fue exitosa.");
        }

        const completeResponse = await AuditService.changeStatus(auditId, 3);
        if (!completeResponse.ok) {
          throw new Error("La respuesta de cambio de estado no fue exitosa.");
        }

        setShowSavedMessage(true);
        window.scrollTo(0, 0);
      } catch (error) {
        console.error(
          "Error durante el proceso de envío de la auditoría:",
          error
        );
        setErrorTitle("Error al Guardar");
        setErrorMessage(
          "Ocurrió un error al guardar o completar la auditoría."
        );
        setShowValidationError(true);
        window.scrollTo(0, 0);
      }
    };

    await wrapWithLoading(submissionProcess)();
  };

  const handleDownloadPdf = async () => {
    setIsExporting(true);
    try {
      const pdfBlob = await ReportService.getReportAuditAsPdf(auditId);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Auditoria_${audit?.policyNumber}_${auditId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      setShowValidationError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadExcel = async () => {
    setIsExporting(true);
    try {
      const excelBlob = await ReportService.getReportAuditAsExcel(auditId);
      const url = URL.createObjectURL(excelBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Auditoria_${audit?.policyNumber}_${auditId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      setShowValidationError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const PdfIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="#D93025"
      viewBox="0 0 16 16"
    >
      <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
      <path d="M4.603 12.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.658-.877.37-.198.816-.22 1.224-.035.516.232.879.67.968 1.212.094.557.022 1.102-.215 1.622a6.6 6.6 0 0 1-.415 1.096c-.086.196-.12.403-.12.614v.035a.5.5 0 0 1-.416.475c-.23.06-.455.11-.675.118-.174.007-.346.036-.501.076a.5.5 0 0 1-.448-.694c.051-.13.11-.266.19-.402.08-.136.166-.27.258-.404a1.8 1.8 0 0 0 .13-.316c.09-.244.09-.557-.036-.803a.97.97 0 0 0-.134-.338.37.37 0 0 0-.122-.182.5.5 0 0 0-.258-.08H8.57a.5.5 0 0 0-.258.08.37.37 0 0 0-.122.182.97.97 0 0 0-.134.338c-.09.244-.09.557.036.803a1.8 1.8 0 0 0 .13.316c.09.136.175.27.258.404.08.136.14.272.19.402a.5.5 0 0 1-.448.694c-.155-.04-.327-.069-.501-.076-.22-.008-.445-.058-.675-.118a.5.5 0 0 1-.416-.475v-.036c0-.211-.035-.418-.12-.614a6.6 6.6 0 0 1-.415-1.096c-.237-.52-.29-1.065-.215-1.622.09-.542.452-.98.968-1.212.407-.185.854-.164 1.224.035.384.205.583.523.658.877.073.34.04.736-.046 1.136a7.269 7.269 0 0 1-.43 1.295c.952.53 1.798 1.24 2.352 2.038a.5.5 0 0 1-.08.704c-.26.262-.63.435-1.08.556-.34.09-.69.16-1.02.215-.33.055-.654.087-1.007.108l-.353.007a.5.5 0 0 1-.42-.592c.094-.455.335-.886.744-1.267.41-.38.918-.695 1.448-.912a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578c-.03-.052-.06-.101-.09-.148a.5.5 0 0 1 .09-.655c.26-.262.59-.415.98-.482a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578.5.5 0 0 1 .09-.655c.25-.247.52-.4.81-.462a.5.5 0 0 1 .578.634l-.51.204a.5.5 0 0 1-.634-.578z" />
    </svg>
  );
  const ExcelIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="#1D6F42"
      viewBox="0 0 16 16"
    >
      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 1.5v2a.5.5 0 0 0 .5.5h2l-2.5-2.5zM5.884 6.68a.5.5 0 1 0-.768.64L7.349 10l-2.233 2.68a.5.5 0 0 0 .768.64L8 10.781l2.116 2.54a.5.5 0 0 0 .768-.64L8.651 10l2.233-2.68a.5.5 0 0 0-.768-.64L8 9.219l-2.116-2.54z" />
    </svg>
  );

  const formatAnswerText = (detail: AuditDetail) => {
    const questionType = detail.auditQuestion?.questionTypeId;
    const answerText = detail.answerText;
    if (questionType === 7 && answerText) {
      const parts = answerText.split("-");
      return parts.length === 3
        ? `${parts[2]}/${parts[1]}/${parts[0]}`
        : answerText;
    }
    if (questionType === 12 && answerText) {
      const number = parseFloat(answerText);
      return isNaN(number)
        ? answerText
        : `$${number.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
    }
    return answerText || detail.auditAnswer?.answerDesc || "Sin respuesta.";
  };

  useEffect(() => {
    if (showValidationError || showSavedMessage) {
      const timer = setTimeout(() => {
        setShowValidationError(false);
        setShowSavedMessage(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showValidationError, showSavedMessage]);

  return (
    <>
      <ModalAuditConfirm show={showModalConfirm} onAgree={handleAgree} />
      <div>
        {isExporting && <LoadingOverlay />}
        <div className="notification-container">
          {showValidationError && (
            <Alert
              variant="danger"
              onClose={() => setShowValidationError(false)}
              className="notification-alert"
              dismissible
            >
              <Alert.Heading>{errorTitle}</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          )}
          {showSavedMessage && (
            <Alert
              variant="success"
              onClose={() => setShowSavedMessage(false)}
              className="notification-alert"
              dismissible
            >
              <Alert.Heading>Notificación</Alert.Heading>
              <p>Completaste la auditoría exitosamente</p>
            </Alert>
          )}
        </div>

        <Container className="pt-3">
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Card>
              <Card.Header className="card-header-main bg-primary text-white">
                <Row>
                  <Col className="fw-bold">Resumen de Auditoría</Col>
                  <Col className="text-end">Póliza: {audit?.policyNumber}</Col>
                </Row>
              </Card.Header>
              <Card.Body>
                {modules.map((module) => (
                  <Card key={module.id} className="mb-3">
                    <Card.Header className="card-header-module bg-secondary text-white p-2 fs-6">
                      {module.name}
                    </Card.Header>
                    {module.sections?.map((section) => (
                      <Card.Body key={section.id}>
                        <Card.Title className="card-header-section bg-primary text-white p-2 rounded fs-6">
                          {section.name}
                        </Card.Title>
                        {section.subsections?.map((subsection) => (
                          <div key={subsection.id} className="ms-3 mt-2">
                            <Card.Subtitle className="mb-2 text-muted fs-6">
                              {subsection.name}
                            </Card.Subtitle>
                            {allQuestions
                              .filter((q) => q.subsectionId === subsection.id)
                              .sort((a, b) => a.position - b.position)
                              .map((questionInfo) => {
                                const detailsForQuestion = auditDetails.filter(
                                  (d) => d.auditQuestionId === questionInfo.id
                                );
                                return (
                                  <div
                                    key={questionInfo.id}
                                    className="mb-3 border-bottom pb-2"
                                  >
                                    <Row>
                                      <Col>
                                        <Form.Text as="div">
                                          <strong>
                                            <Badge
                                              bg="primary"
                                              pill
                                              className="me-2"
                                            >
                                              {questionInfo.position}
                                            </Badge>
                                            {questionInfo.questionDesc}
                                          </strong>
                                        </Form.Text>
                                      </Col>
                                    </Row>
                                    {detailsForQuestion.length > 0 ? (
                                      detailsForQuestion.map((detail) => {
                                        const isTableType = [4, 8].includes(
                                          detail.auditQuestion
                                            ?.questionTypeId || 0
                                        );
                                        return isTableType ? (
                                          <React.Fragment key={detail.id}>
                                            <Row className="mt-2">
                                              <Col xs={12}>
                                                {detail.auditQuestion
                                                  ?.questionTypeId === 4 &&
                                                  renderMultiColumnTable(
                                                    detail
                                                  )}
                                                {detail.auditQuestion
                                                  ?.questionTypeId === 8 &&
                                                  renderMatrixTable(detail)}
                                              </Col>
                                            </Row>
                                            <Row className="mt-2 justify-content-end">
                                              <Col xs={12} md={6}>
                                                <Form.Control
                                                  type="text"
                                                  placeholder="Agregar comentario..."
                                                  maxLength={500}
                                                  value={detail.comment || ""}
                                                  onChange={(e) =>
                                                    handleCommentChanged(
                                                      detail.id,
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </Col>
                                            </Row>
                                          </React.Fragment>
                                        ) : (
                                          <Row
                                            key={detail.id}
                                            className="mt-2 align-items-center"
                                          >
                                            <Col xs={12} md={6}>
                                              <Form.Text
                                                className={
                                                  detail.answerText ||
                                                  detail.auditAnswer?.answerDesc
                                                    ? ""
                                                    : "text-muted fst-italic"
                                                }
                                              >
                                                {formatAnswerText(detail)}
                                              </Form.Text>
                                            </Col>
                                            <Col xs={12} md={6}>
                                              <Form.Control
                                                type="text"
                                                placeholder="Agregar comentario..."
                                                maxLength={500}
                                                value={detail.comment || ""}
                                                onChange={(e) =>
                                                  handleCommentChanged(
                                                    detail.id,
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </Col>
                                          </Row>
                                        );
                                      })
                                    ) : (
                                      <Row className="mt-2">
                                        <Col>
                                          <Form.Text className="text-muted fst-italic">
                                            Sin respuesta.
                                          </Form.Text>
                                        </Col>
                                      </Row>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </Card.Body>
                    ))}
                  </Card>
                ))}
                <div style={{ height: "5rem" }} />
              </Card.Body>
              <Card.Footer className="text-center fixed-bottom bg-light border-top py-3">
                <Stack
                  direction="horizontal"
                  gap={3}
                  className="justify-content-center"
                >
                  <Button variant="secondary" onClick={() => navigate(-1)}>
                    <FontAwesomeIcon icon={faCaretLeft} className="me-2" />
                    Regresar
                  </Button>

                  <Button variant="primary" type="submit">
                    <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                    Completar
                  </Button>
                  {user?.role === "Administrador" && (
                    <>
                      <div className="vr" />
                      <span>Exportar a </span>
                      <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center"
                        title="Descargar PDF"
                        onClick={handleDownloadPdf}
                      >
                        <PdfIcon />
                        <span className="ms-1">PDF</span>
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center"
                        title="Descargar Excel"
                        onClick={handleDownloadExcel}
                      >
                        <ExcelIcon />
                        <span className="ms-1">Excel</span>
                      </Button>
                    </>
                  )}
                </Stack>
              </Card.Footer>
            </Card>
          </Form>
        </Container>
      </div>
    </>
  );
};

export default Summary;
